import React from 'react';
import { Card } from './Card';

type MetricVariant = 'default' | 'health' | 'ecosystem' | 'economic' | 'quantum';

interface MetricCardProps {
  label: string;
  value: string | number;
  unit?: string;
  subValue?: string;
  trend?: {
    value: number;
    label?: string;
  };
  variant?: MetricVariant;
  /**
   * 'burden' overrides the accent to amber to flag a net negative
   * (a net lifecycle burden), matching the project-level impact views.
   */
  tone?: 'normal' | 'burden';
  icon?: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const variantClasses: Record<MetricVariant, { glow: string; accent: string }> = {
  default: { glow: '', accent: 'text-text-primary' },
  health: { glow: 'hover:shadow-glow-health', accent: 'text-health-400' },
  ecosystem: { glow: 'hover:shadow-glow-ecosystem', accent: 'text-ecosystem-400' },
  economic: { glow: 'hover:shadow-glow-economic', accent: 'text-economic-400' },
  quantum: { glow: 'hover:shadow-glow-quantum', accent: 'text-quantum-400' },
};

const sizeClasses = {
  sm: { value: 'text-h2', label: 'text-caption' },
  md: { value: 'text-metric', label: 'text-body-sm' },
  lg: { value: 'text-metric-lg', label: 'text-body' },
};

export function MetricCard({
  label,
  value,
  unit,
  subValue,
  trend,
  variant = 'default',
  tone = 'normal',
  icon,
  className = '',
  size = 'md',
}: MetricCardProps) {
  const { glow, accent: variantAccent } = variantClasses[variant];
  const accent = tone === 'burden' ? 'text-amber-400' : variantAccent;
  const sizes = sizeClasses[size];

  return (
    <Card className={`${glow} transition-shadow duration-300 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className={`${sizes.label} text-text-secondary mb-2`}>{label}</p>
          <div className="flex items-baseline gap-2">
            <span className={`${sizes.value} ${accent}`}>
              {typeof value === 'number' ? formatNumber(value) : value}
            </span>
            {unit && <span className="text-body-sm text-text-muted">{unit}</span>}
          </div>
          {subValue && (
            <p className="text-caption text-text-muted mt-1">{subValue}</p>
          )}
          {trend && (
            <div className={`mt-2 flex items-center gap-1 ${trend.value >= 0 ? 'text-quantum-400' : 'text-health-400'}`}>
              <TrendIcon direction={trend.value >= 0 ? 'up' : 'down'} />
              <span className="text-caption font-medium">
                {trend.value >= 0 ? '+' : ''}{trend.value}%
              </span>
              {trend.label && (
                <span className="text-caption text-text-muted">{trend.label}</span>
              )}
            </div>
          )}
        </div>
        {icon && (
          <div className={`p-2 rounded-lg bg-surface-600 ${accent}`}>
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
}

function TrendIcon({ direction }: { direction: 'up' | 'down' }) {
  return direction === 'up' ? (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
    </svg>
  );
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// Impact metrics grid for displaying health/ecosystem/economic together
interface ImpactMetricsProps {
  health: { value: number; unit: string; label: string };
  ecosystem: { value: number; unit: string; label: string };
  economic: { value: number; unit: string; label: string };
  className?: string;
}

export function ImpactMetrics({ health, ecosystem, economic, className = '' }: ImpactMetricsProps) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${className}`}>
      <MetricCard
        label={health.label}
        value={health.value}
        unit={health.unit}
        variant="health"
        icon={<HeartIcon />}
      />
      <MetricCard
        label={ecosystem.label}
        value={ecosystem.value}
        unit={ecosystem.unit}
        variant="ecosystem"
        icon={<LeafIcon />}
      />
      <MetricCard
        label={economic.label}
        value={economic.value}
        unit={economic.unit}
        variant="economic"
        icon={<DollarIcon />}
      />
    </div>
  );
}

// Simple icons for the metrics
function HeartIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

function LeafIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function DollarIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
